import type { WallpaperConfig } from '../config/schema';
import { computeDotGrid, resolveSafeAreas } from '../lib/grid';
import { Dot } from '../components/Dot';
import {
  WEEKDAY_LETTERS_MON,
  WEEKDAY_LETTERS_SUN,
  MONTH_LABELS,
  isSameDay,
  yearGrid,
} from '../lib/dates';

interface Props {
  config: WallpaperConfig;
  today: Date;
  titleHeight: number;
}

export function YearlyGrid({ config, today, titleHeight }: Props) {
  const { yearly, device, padding, colors, shape, highlightToday, showLabels } = config;
  const year = yearly.year ?? today.getFullYear();
  const weekStartsOn = yearly.weekStartDay === 'sunday' ? 0 : 1;
  const { cells, weeks } = yearGrid(year, weekStartsOn);
  const weeksPerRow = yearly.weeksPerRow;

  const topLabelHeight = showLabels ? 60 : 0;
  const leftLabelGutter = showLabels ? 70 : 0;

  const { clockReserved, bottomReserved } = resolveSafeAreas(padding, device);

  const rows = weeksPerRow === 2 ? Math.ceil(weeks / 2) : weeks;
  const cols = weeksPerRow * 7;

  const layout = computeDotGrid({
    width: device.width,
    height: device.height,
    paddingH: padding.horizontal,
    paddingV: padding.vertical,
    rows,
    cols,
    topReserved: titleHeight + clockReserved + topLabelHeight,
    bottomReserved,
    leftReserved: leftLabelGutter,
    rightReserved: leftLabelGutter,
    groupEveryCol: weeksPerRow > 1 ? 7 : 0,
    colGroupGapRatio: 1.0,
  });

  const cellGridPos = (weekIndex: number, dayIndex: number) => ({
    row: weeksPerRow === 2 ? Math.floor(weekIndex / 2) : weekIndex,
    col: weeksPerRow === 2 ? (weekIndex % 2) * 7 + dayIndex : dayIndex,
  });

  const dots = cells.map((cell) => {
    const isPast = cell.date < today && !isSameDay(cell.date, today);
    const isToday = isSameDay(cell.date, today);
    const color =
      isToday && highlightToday
        ? colors.today
        : isPast
          ? colors.filled
          : colors.empty;
    const { row, col } = cellGridPos(cell.weekIndex, cell.dayIndex);
    const { cx, cy } = layout.cellPosition(row, col);
    return (
      <Dot
        key={cell.date.toISOString()}
        cx={cx}
        cy={cy}
        r={layout.dotRadius}
        color={color}
        shape={shape}
      />
    );
  });

  const letters = weekStartsOn === 1 ? WEEKDAY_LETTERS_MON : WEEKDAY_LETTERS_SUN;
  const labelLetters = weeksPerRow === 2 ? [...letters, ...letters] : letters;
  const dayLabels = showLabels
    ? labelLetters.map((letter, idx) => {
        const { cx } = layout.cellPosition(0, idx);
        return (
          <text
            key={`label-${idx}`}
            x={cx}
            y={layout.originY - layout.pitchY / 2 - layout.dotRadius * 0.6}
            fill={colors.text}
            fontFamily="ui-sans-serif, system-ui, -apple-system"
            fontSize={layout.dotRadius}
            textAnchor="middle"
            opacity={0.5}
          >
            {letter}
          </text>
        );
      })
    : null;

  const seenMonths = new Set<number>();
  const monthLabelPositions = cells
    .filter((cell) => {
      const m = cell.date.getMonth();
      if (seenMonths.has(m)) return false;
      seenMonths.add(m);
      return true;
    })
    .map((cell) => {
      const { row } = cellGridPos(cell.weekIndex, cell.dayIndex);
      const { cy } = layout.cellPosition(row, 0);
      return { month: cell.date.getMonth(), cy };
    });

  const monthLabels = showLabels
    ? monthLabelPositions.map(({ month, cy }) => (
        <text
          key={`month-${month}`}
          x={layout.originX - layout.pitchX / 2 - layout.dotRadius * 0.6}
          y={cy + layout.dotRadius * 0.35}
          fill={colors.text}
          fontFamily="ui-sans-serif, system-ui, -apple-system"
          fontSize={layout.dotRadius}
          textAnchor="end"
          opacity={0.5}
        >
          {MONTH_LABELS[month]}
        </text>
      ))
    : null;

  return (
    <g data-mode="yearly">
      {dots}
      {dayLabels}
      {monthLabels}
    </g>
  );
}
